import { ButtonWithLoader, success } from '@valid-eval/shared-react-components';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import GenericModal from 'components/GenericModal';
import { useCommunicationBySlug } from 'data/features/communicationsHooks';
import { useFetchEventUsers, useSubmitForReview } from 'data/features/eventsHooks';
import { Event } from 'data/features/eventsTypes';
import useBooleanFlag from 'utils/hooks/useBooleanFlag';
import { useShowErrorIfRejected } from 'utils/hooks/useShowErrorIfRejected';

import { useNavigate } from 'react-router';
import SubmitForReviewForm, { SubmitForReviewFormData } from './SubmitForReviewForm';

type SubmitForReviewButtonProps = {
  loading?: boolean;
  event: Event;
  beforeSubmit?: () => void;
};

function SubmitForReviewButton({
  loading = false,
  event,
  beforeSubmit,
}: SubmitForReviewButtonProps) {
  const { t } = useTranslation();
  const [showModal, openModal, closeModal] = useBooleanFlag(false);
  const { organizers } = useFetchEventUsers(event.id);
  const { submitEventForReview, saving } = useSubmitForReview();
  const communication = useCommunicationBySlug('event_submit_for_review', event.id);
  const showErrorIfRejected = useShowErrorIfRejected();
  const navigate = useNavigate();

  const submitForm = useForm<SubmitForReviewFormData>({
    mode: 'all',
    values: {
      organizer: organizers[0]?.id,
      subject: communication?.subject ?? '',
      content: communication?.content ?? '',
    },
  });

  const handleClick = async () => {
    await beforeSubmit?.();
    openModal();
  };

  const handleFormSubmit = async (data: SubmitForReviewFormData) => {
    const [, hasError] = showErrorIfRejected(
      await submitEventForReview(event.id, data.organizer, data.subject, data.content),
    );
    if (!hasError) {
      success(t('event.form.submit_for_review_success'));
      navigate('/');
    }
  };

  return (
    <>
      <ButtonWithLoader
        variant="primary"
        type="button"
        loading={loading || saving}
        onClick={handleClick}
      >
        {t('event.form.submit_for_review')}
      </ButtonWithLoader>

      <GenericModal
        show={showModal}
        onCancel={closeModal}
        onConfirm={submitForm.handleSubmit(handleFormSubmit)}
        title={t('event.form.submit_for_review')}
        body={
          <SubmitForReviewForm
            methods={submitForm}
            onSubmit={handleFormSubmit}
            organizers={organizers}
            template={communication}
          />
        }
        name="submit-for-review"
        size="lg"
        isButtonDisabled={saving}
        isCancelButtonDisabled={saving}
      />
    </>
  );
}

export default SubmitForReviewButton;
